/* DemoTry — public PIN-entry page that unlocks the sandbox demo.
 *
 * Recruiters share a 6-digit PIN with peers/candidates; this page accepts
 * the PIN and forwards them straight into the training sandbox so they
 * can try ALL 5 scenarios (Turkey domestic worker, France family + maid,
 * Saudi resident → Germany, Canada BIL, etc.).
 *
 * No VTA branding. No login. No company identifiers.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck } from "lucide-react";
import { api } from "@/api/client";

const C = {
  bg: "#0a0a0f",
  card: "#13131a",
  border: "#2a2a35",
  accent: "#d4af37",
  accentSoft: "rgba(212,175,55,0.15)",
  ink: "#f4f4f5",
  sub: "#9a9a9f",
  danger: "#fca5a5",
};

export default function DemoTry() {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    const clean = pin.replace(/\D/g, "");
    if (clean.length !== 6) { setErr("الرمز يتكوّن من 6 أرقام"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ats/demo-redeem", { pin: clean });
      navigate(data.training_url, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر التحقّق من الرمز");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: C.bg, color: C.ink, display: "flex",
      alignItems: "center", justifyContent: "center", padding: 16,
      fontFamily: "system-ui, -apple-system, 'Segoe UI', Tahoma, sans-serif",
      direction: "rtl",
    }} data-testid="demo-try-page">
      <div style={{
        width: "100%", maxWidth: 420, background: C.card,
        border: `1px solid ${C.border}`, borderRadius: 16, padding: 28,
        boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: C.accentSoft, color: C.accent,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <ClipboardCheck size={30} strokeWidth={2.2} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 900, marginTop: 12, marginBottom: 4 }}>
            بيئة التقييم العملي
          </h1>
          <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.7 }}>
            أدخل الرمز السرّي المُرسَل إليك للوصول إلى بيئة التقييم.<br/>
            ستتاح لك عدّة سيناريوهات (فردي / عائلة / عاملة / مقيم …) لتطبيق الإدخال عليها.
          </div>
        </div>

        <form onSubmit={submit}>
          <label style={{ fontSize: 12, color: C.sub, fontWeight: 700, display: "block", marginBottom: 6 }}>
            الرمز السري (6 أرقام)
          </label>
          <input
            type="tel" inputMode="numeric" maxLength={6} autoFocus
            value={pin}
            onChange={(e) => { setErr(""); setPin(e.target.value.replace(/\D/g, "")); }}
            placeholder="••••••"
            data-testid="demo-pin-input"
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "14px 16px", fontSize: 22, letterSpacing: 6,
              textAlign: "center", borderRadius: 10,
              background: "#0a0a0f", border: `1.5px solid ${C.border}`,
              color: C.ink, outline: "none", fontFamily: "monospace",
            }}
          />
          {err && (
            <div style={{ color: C.danger, fontSize: 13, marginTop: 10, textAlign: "center" }}
                 data-testid="demo-pin-error">{err}</div>
          )}
          <button
            type="submit" disabled={busy || pin.length !== 6}
            style={{
              width: "100%", marginTop: 16,
              padding: "12px 16px", border: "none", borderRadius: 10,
              background: pin.length === 6 ? C.accent : C.border,
              color: "#0a0a0f", fontWeight: 900, fontSize: 15,
              cursor: busy ? "wait" : (pin.length === 6 ? "pointer" : "not-allowed"),
              opacity: busy ? 0.7 : 1,
            }}
            data-testid="demo-pin-submit">
            {busy ? "جارٍ التحقّق…" : "بدء التقييم"}
          </button>
        </form>

        <div style={{ fontSize: 11.5, color: C.sub, marginTop: 18, textAlign: "center", lineHeight: 1.7 }}>
          الرمز صالح لمدة 24 ساعة من إصداره.<br/>
          بيئة تقييم خاصّة — لا توجد تكاليف ولا التزامات.
        </div>
      </div>
    </div>
  );
}
